import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Menu,
  Button,
  MenuItem,
  Tooltip,
  IconButton,
  ListItemIcon,
  ListItemText
} from "@material-ui/core";
import {
  Add,
  AddTask,
  HistoryEdu,
  EventOutlined,
  NoteAddOutlined,
  KeyboardArrowDown,
  WhatsApp
} from "@material-ui/icons";
import ScheduledTemplateSendDialog from "../../whatsappOfficial/scheduled/ScheduledTemplateSendDialog";
import useCalendar from "hooks/useCalendar";
import useFirestore from "hooks/useFirestore";
import { parseContactsToAttendees } from "utils/calendar/helper";
import useContactHandler from "hooks/crm/useContactHandler";
import CrmHandlerGuardDialog from "../../crm/handler/CrmHandlerGuardDialog";
import { acknowledgeHandlerGuard, handlerGuardAcknowledged } from "../../crm/handler/crmHandlerGuard";
import { buildNewSignatureParams } from "../../signatures/GlobalNewSignatureDialog";
import { quickPickRoleForContact } from "../../signatures/NewSignatureForm";
import type { RealEstateContact } from "../../../../@types/sharedSchema";

/**
 * The per-contact quick actions — קביעת פגישה / משימה חדשה / הוספת הערה / מסמך לחתימה —
 * as ONE menu behind either of two triggers.
 *
 * IT LIVES IN ITS OWN FILE BECAUSE IT HAS TWO HOSTS. `ContactDetailHeader` is the full
 * customer page's header, and the CRM side panel deliberately does NOT render that header —
 * it has a compact one of its own — so until now these four actions were reachable from the
 * full page and from nowhere else (pilot, 2026-08-25: "side panel — add a + button to open
 * the 'פעולות' dropdown"). Copying the four handlers into the panel would have let the two
 * surfaces drift apart the first time one gained an action or changed a feature gate, and
 * the signature item is exactly the kind of entry that gets added to one and not the other.
 *
 * TWO TRIGGERS, ONE MENU. `variant="button"` is the full page's contained "פעולות" text
 * button; `variant="icon"` is the panel's compact `+`, where the header's action cluster is
 * a row of square icon buttons beside a name and a labelled button has nowhere to go.
 *
 * ⚠️ DISABLED UNTIL THE DOCUMENT IS IN HAND, AND THAT RULE LIVES HERE so that no host can
 * forget it. Every item is seeded from the contact document — the attendees from its name
 * and phone, the signature role from its customer types — and the CRM panel renders its
 * header from a ROW PROJECTION while the live read is still in flight. A null `contact` is
 * therefore the normal early state and not a caller error: the trigger renders disabled,
 * exactly like the edit button standing next to it.
 *
 * ⚠️ IT SUBSCRIBES TO THE LOCATION CONTEXT. `useSearchParams` re-renders its holder on every
 * search-param write anywhere on the page. That is fine for the ONE page header or panel
 * header that hosts this, and would NOT be fine inside the 50 mounted CRM rows — see the
 * header of `crm/panel/crmPanelUrl.ts` for the rule and `crm/menu/CrmRowMenuContent` for the
 * row-safe shape (read `window.location` imperatively at click time, push through the
 * router's stable `navigator`). Keeping the hook in here rather than in the hosts also
 * narrows the subscription to the trigger itself: neither `ContactDetailHeader` nor
 * `CrmContactPanel` re-renders any more because the URL moved.
 */
type ContactQuickActionsMenuProps = {
  /**
   * Null/undefined while the host is still rendering from a projection — see the
   * disabled-until-loaded rule above.
   */
  contact: RealEstateContact | null | undefined;
  /** Which trigger to draw. Defaults to the full page's labelled button. */
  variant?: "button" | "icon";
  /**
   * `icon` only: the square edge, so the trigger matches whichever action cluster it joins —
   * 36 beside the panel's docked icon row, 44 among the sheet's touch targets.
   */
  iconSize?: number;
};

export function ContactQuickActionsMenu({
  contact,
  variant = "button",
  iconSize = 36
}: ContactQuickActionsMenuProps): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const { userSettings } = useFirestore();
  const { showSingleEventEditorModalByType } = useCalendar();

  const [quickActionsAnchor, setQuickActionsAnchor] = useState<null | HTMLElement>(null);

  /**
   * THE SOFT GUARDRAIL, AT THE ENTRY POINT RATHER THAN AT THE SAVE.
   *
   * This menu is the single host for קביעת פגישה / משימה חדשה / מסמך לחתימה across BOTH the
   * full customer page and the CRM side panel, which is exactly why the guard belongs here:
   * one mount, three actions, both surfaces.
   *
   * It fires BEFORE the editor opens, not on its save. A realtor who has already filled in a
   * meeting should not then be told the lead is somebody else's — and blocking a calendar
   * SAVE would be a real block, which the product decision rules out (an event legitimately
   * has several attendees, only one of whom may be designated).
   *
   * ⚠️ IT NEVER FIRES ON AN UNANSWERED LOOKUP. `handler === undefined` means the request has
   * not landed (or failed, or was never issued); the worst case there is a missing warning,
   * never a stuck user. It also never fires on the viewer's own designation.
   */
  const contactId = contact?.id ?? null;
  const { handler } = useContactHandler(contactId);
  const collidingHandler = handler && !handler.isMe && !handler.stale ? handler : null;
  const [guardedAction, setGuardedAction] = useState<null | (() => void)>(null);

  /**
   * Wraps one action in the guard. Returns the original when there is nothing to warn about,
   * so the common path costs no indirection at all.
   */
  const guarded = (run: () => void) => () => {
    if (!collidingHandler || !contactId || handlerGuardAcknowledged(contactId)) {
      run();
      return;
    }
    // Stored as a thunk-returning setter — `useState` calls a bare function argument.
    setGuardedAction(() => run);
  };

  const hasSignatureFeatureFlag = userSettings?.featureFlagsV2?.includes("signature") ?? false;
  // Inline gate, per the feature-gating rule: this menu serves both worlds, only the WhatsApp
  // template item is officialWhatsapp-only.
  const hasOfficialWhatsapp = userSettings?.advancedFeatures?.includes("officialWhatsapp") ?? false;
  const [templateSendOpen, setTemplateSendOpen] = useState(false);

  const handleNewMeeting = (): void => {
    if (!contact) return;
    showSingleEventEditorModalByType("meeting", {
      attendees: parseContactsToAttendees([contact])
    });
  };

  const handleNewTask = (): void => {
    if (!contact) return;
    // Same prefill convention as meetings — the calendar editor in task mode.
    showSingleEventEditorModalByType("task", {
      attendees: parseContactsToAttendees([contact])
    });
  };

  const handleAddNote = (): void => {
    // Guarded like its three siblings even though it never dereferences `contact` — it
    // only merges a param. `strictNullChecks` is OFF in this tsconfig, so the compiler
    // would not catch a future edit that started reading the contact here; uniformity is
    // what makes that edit inherit the protection instead of needing to remember it.
    if (!contact) return;
    // The notes tab opens with its composer at the top — deep-link to it via
    // the ?tab= param (MERGE, never rebuild — the URL is shared with the
    // global signature dialog params). "tab" mirrors ContactDetailTabs'
    // CONTACT_DETAIL_TAB_PARAM (duplicated by convention — see TimelineTab).
    const next = new URLSearchParams(searchParams);
    next.set("tab", "notes");
    setSearchParams(next, { replace: true });
  };

  const handleSendDocumentToSign = (): void => {
    if (!contact) return;
    // Opens the globally-mounted new-signature dialog via URL search params —
    // same mechanism as the contacts-table row menu; no navigation involved.
    const next = new URLSearchParams(searchParams);
    const params = buildNewSignatureParams({
      contactId: contact.id,
      role: quickPickRoleForContact(contact) ?? undefined
    });
    Object.entries(params).forEach(([k, v]) => next.set(k, v));
    setSearchParams(next);
  };

  const runItem = (action: () => void) => (): void => {
    setQuickActionsAnchor(null);
    action();
  };

  const trigger =
    variant === "icon" ? (
      <Tooltip title="פעולות מהירות">
        {/* `span` because a disabled button fires no events, and MUI's Tooltip needs one to
            know it is being hovered — the same idiom the panel's edit button uses. It is
            also the flex child wherever this joins a row, hence `flexShrink: 0`: the
            IconButton's `width` is a size, not a floor, and a shrinking wrapper would take
            the touch target below the 44px the sheet's row is built on. */}
        <Box component="span" sx={{ display: "inline-flex", flexShrink: 0 }}>
          <IconButton
            size="small"
            disabled={!contact}
            onClick={(e) => setQuickActionsAnchor(e.currentTarget)}
            aria-label="פעולות מהירות"
            aria-haspopup="menu"
            sx={{ width: iconSize, height: iconSize }}
          >
            <Add sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Tooltip>
    ) : (
      <Button
        variant="contained"
        size="small"
        disabled={!contact}
        startIcon={<Add />}
        endIcon={<KeyboardArrowDown sx={{ fontSize: 18 }} />}
        onClick={(e) => setQuickActionsAnchor(e.currentTarget)}
        aria-label="פעולות מהירות"
        aria-haspopup="menu"
        sx={{ fontWeight: 700, flexShrink: 0, borderRadius: "10px", height: { xs: 44, md: 34 } }}
      >
        פעולות
      </Button>
    );

  return (
    <>
      {trigger}
      <Menu
        anchorEl={quickActionsAnchor}
        open={!!quickActionsAnchor}
        onClose={() => setQuickActionsAnchor(null)}
      >
        <MenuItem onClick={runItem(guarded(handleNewMeeting))}>
          <ListItemIcon>
            <EventOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>קביעת פגישה</ListItemText>
        </MenuItem>
        <MenuItem onClick={runItem(guarded(handleNewTask))}>
          <ListItemIcon>
            <AddTask fontSize="small" />
          </ListItemIcon>
          <ListItemText>משימה חדשה</ListItemText>
        </MenuItem>
        <MenuItem onClick={runItem(handleAddNote)}>
          <ListItemIcon>
            <NoteAddOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>הוספת הערה</ListItemText>
        </MenuItem>
        {hasSignatureFeatureFlag && (
          <MenuItem onClick={runItem(guarded(handleSendDocumentToSign))}>
            <ListItemIcon>
              {/* HistoryEdu (quill-on-document) — visually distinct from the edit pencil */}
              <HistoryEdu fontSize="small" />
            </ListItemIcon>
            <ListItemText>מסמך לחתימה</ListItemText>
          </MenuItem>
        )}
        {hasOfficialWhatsapp && (
          <MenuItem onClick={runItem(guarded(() => setTemplateSendOpen(true)))}>
            <ListItemIcon>
              <WhatsApp fontSize="small" />
            </ListItemIcon>
            <ListItemText>שליחת תבנית וואטסאפ</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Mounted only after the item was chosen — the dialog loads templates on open. */}
      {hasOfficialWhatsapp && templateSendOpen && contact && (
        <ScheduledTemplateSendDialog
          open
          contact={{
            phone: contact.id,
            firstName: contact.callerFirstName ?? undefined,
            lastName: contact.callerLastName ?? undefined,
            displayName: contact.contactFullName ?? undefined
          }}
          onClose={() => setTemplateSendOpen(false)}
        />
      )}

      {/* One dialog for all three actions — they ask the same question about the same
          person, and the answer is remembered for the rest of the session so a realtor who
          books a meeting and then a follow-up task is not asked twice. */}
      {guardedAction && collidingHandler && contactId && (
        <CrmHandlerGuardDialog
          open
          handler={collidingHandler}
          contactId={contactId}
          displayName={contact?.contactFullName ?? null}
          onCancel={() => setGuardedAction(null)}
          onContinue={() => {
            acknowledgeHandlerGuard(contactId);
            const run = guardedAction;
            setGuardedAction(null);
            run();
          }}
        />
      )}
    </>
  );
}

export default ContactQuickActionsMenu;
